<div [hidden]="isInitialLoading">
    @if (metricsLoadError) {
        <div class="alert alert-warning py-2 small" role="alert">
            Some dashboard metrics could not be loaded. The due queue may still be available below.
        </div>
    }

    <div class="row g-3 mb-3">
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-primary border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">JHAs submitted (30 days)</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.jhasSubmittedLast30Days }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-warning border-opacity-50">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Open non-compliant items</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.openNonCompliantItems }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100 border-success border-opacity-25">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Items marked corrected (90 days)</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.itemsMarkedCorrectedLast90Days }}</div>
                </div>
            </div>
        </div>
        <div class="col-6 col-lg-3">
            <div class="card h-100">
                <div class="card-body py-3">
                    <div class="text-muted small text-uppercase">Submitted JHAs (6 mo)</div>
                    <div class="fs-3 fw-semibold">{{ metrics.kpi.submittedJhasLast6Months }}</div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-3">
        <div class="col-12">
            <div class="card border-danger border-opacity-50 jha-due-queue-card">
                <div class="card-body pb-2">
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h5 class="card-title mb-0">Jobs due for JHA</h5>
                        <span class="badge rounded-pill text-bg-danger">{{ dueCount }}</span>
                    </div>
                    <p class="small text-muted mb-2">
                        Due when a job reaches 1,200 labor hours or 1 calendar month since the last submitted JHA.
                    </p>
                    @if (dueQueueLoadError) {
                        <p class="text-danger small mb-0">Unable to load the JHA due queue. Try refreshing the page.</p>
                    } @else if (jobsDueForJha.length === 0) {
                        <p class="text-muted small mb-0">No active jobs are currently due for a JHA.</p>
                    } @else {
                        <div class="table-responsive jha-due-queue-scroll">
                            <table class="table table-sm table-hover mb-0">
                                <thead class="jha-due-queue-thead">
                                    <tr>
                                        <th>Job</th>
                                        <th>Site superintendent</th>
                                        <th>Last JHA date</th>
                                        <th class="text-end">Hours since last JHA</th>
                                        <th>Due reason(s)</th>
                                        <th>Due by date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of jobsDueForJha; track row.jobId) {
                                        <tr [class.jha-due-row-urgent]="isUrgentDueRow(row)">
                                            <td>
                                                @if (canEdit) {
                                                    <button
                                                        type="button"
                                                        class="btn btn-link btn-sm p-0 align-baseline"
                                                        title="Create JHA for this job"
                                                        (click)="addForJob.emit({ jobId: row.jobId, jobName: row.jobName })"
                                                    >
                                                        {{ formatJobDisplay(row.jobId, row.jobName) }}
                                                    </button>
                                                } @else {
                                                    {{ formatJobDisplay(row.jobId, row.jobName) }}
                                                }
                                            </td>
                                            <td>{{ row.siteSuperName || '—' }}</td>
                                            <td>
                                                @if (row.lastJhaDate) {
                                                    {{ row.lastJhaDate | date: 'mediumDate' }}
                                                    @if (row.daysSinceLastJha != null) {
                                                        <span class="text-muted small"> ({{ row.daysSinceLastJha }} days)</span>
                                                    }
                                                } @else {
                                                    <span class="text-muted">—</span>
                                                }
                                            </td>
                                            <td class="text-end">{{ formatHours(row.hoursSinceLastJha) }}</td>
                                            <td class="small">{{ formatDueReasons(row.dueReasons) }}</td>
                                            <td>
                                                @if (row.dueByDate) {
                                                    {{ row.dueByDate | date: 'mediumDate' }}
                                                } @else {
                                                    <span class="text-muted">—</span>
                                                }
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3">
        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Open non-compliant items by job</h5>
                    <p class="small text-muted">Submitted JHAs with checklist items still marked non-compliant and not corrected.</p>
                    @if (metrics.openNcByJob.length === 0) {
                        <p class="text-muted small mb-0">No open non-compliant items.</p>
                    } @else {
                        <div class="table-responsive">
                            <table class="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th>Job</th>
                                        <th class="text-end">Open items</th>
                                        @if (canViewJhaList) {
                                            <th></th>
                                        }
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (row of metrics.openNcByJob; track row.jobId) {
                                        <tr>
                                            <td>{{ formatJobDisplay(row.jobId, row.jobName) }}</td>
                                            <td class="text-end">{{ row.openItems }}</td>
                                            @if (canViewJhaList) {
                                                <td class="text-end">
                                                    <a
                                                        routerLink="/safety/job-hazard-analysis"
                                                        [queryParams]="jhaListQueryParamsForFocusJha(row.focusJhaId)"
                                                        class="small"
                                                    >
                                                        View JHA
                                                    </a>
                                                </td>
                                            }
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="card h-100">
                <div class="card-body">
                    <h5 class="card-title">Items marked corrected by month</h5>
                    <p class="small text-muted">Correction events from submitted JHAs over the last six months.</p>
                    @if (metrics.correctionsByMonth.length === 0) {
                        <p class="text-muted small mb-0">No correction events in this period.</p>
                    } @else {
                        <div class="d-flex align-items-end gap-1 jha-bar-wrap">
                            @for (m of metrics.correctionsByMonth; track m.month) {
                                <div class="flex-fill text-center">
                                    <div
                                        class="jha-bar rounded-top"
                                        [style.height.px]="(m.count / maxResolvedCount()) * 120 || 4"
                                        [title]="m.month + ': ' + m.count"
                                    ></div>
                                    <div class="small text-muted mt-1">{{ m.month }}</div>
                                    <div class="small fw-semibold">{{ m.count }}</div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">JHAs submitted by job × month</h5>
                    <p class="small text-muted">Submission density by job over the last six months (top jobs by volume).</p>
                    @if (metrics.submissionHeatmap.rows.length === 0) {
                        <p class="text-muted small mb-0">No submitted JHAs in this period.</p>
                    } @else {
                        <div class="table-responsive">
                            <table class="table table-bordered table-sm text-center mb-0 jha-heatmap">
                                <thead>
                                    <tr>
                                        <th class="text-start">Job</th>
                                        @for (mo of metrics.submissionHeatmap.months; track mo) {
                                            <th class="small">{{ mo }}</th>
                                        }
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (r of metrics.submissionHeatmap.rows; track r.label) {
                                        <tr>
                                            <td class="text-start small">{{ r.label }}</td>
                                            @for (v of r.values; track $index) {
                                                <td class="small p-2" [style.background-color]="heatmapColor(v)">{{ v }}</td>
                                            }
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>

        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">Open corrective work aging</h5>
                    <p class="small text-muted">
                        Jobs with non-compliant items still open. Days are measured from JHA submission (or re-open after inspector revert).
                    </p>
                    @if (metrics.correctiveAging.length === 0) {
                        <p class="text-muted small mb-0">No open corrective work.</p>
                    } @else {
                        <div class="table-responsive">
                            <table class="table table-sm table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Job</th>
                                        <th>Site superintendent</th>
                                        <th class="text-end">Days since first open item</th>
                                        <th class="text-end">Open items</th>
                                        @if (canViewJhaList) {
                                            <th></th>
                                        }
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (a of metrics.correctiveAging; track a.jobId) {
                                        <tr>
                                            <td>{{ formatJobDisplay(a.jobId, a.jobName) }}</td>
                                            <td>{{ a.siteSuper || '—' }}</td>
                                            <td class="text-end">{{ a.daysOpen }}</td>
                                            <td class="text-end">{{ a.openItemCount }}</td>
                                            @if (canViewJhaList) {
                                                <td class="text-end">
                                                    <a
                                                        routerLink="/safety/job-hazard-analysis"
                                                        [queryParams]="jhaListQueryParamsForFocusJha(a.focusJhaId)"
                                                        class="small"
                                                    >
                                                        View JHA
                                                    </a>
                                                </td>
                                            }
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
</div>
